import { io, Socket } from "socket.io-client";
import { API_URL } from "@/lib/config";
import AsyncStorage from "@react-native-async-storage/async-storage";

class NotificationSocketService {
  private socket: Socket | null = null;
  private userId: string | null = null;
  private notificationCallbacks: Map<
    string,
    (data: { notification: any }) => void
  > = new Map();
  private unreadCountCallbacks: Map<
    string,
    (data: { unreadCount: number }) => void
  > = new Map();

  /**
   * Connect to the notification WebSocket namespace and identify the user.
   */
  initializeNotificationSocket(userId: string) {
    if (this.socket?.connected && this.userId === userId) return;

    this.userId = userId;

    this.socket = io(`${API_URL}/notifications`, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on("connect", async () => {
      const token = await AsyncStorage.getItem("accessToken");
      this.socket?.emit("user:identify", { userId, token });
    });

    this.socket.on(
      "notification:new",
      (data: { notification: any }) => {
        this.notificationCallbacks.forEach((callback) => {
          try {
            callback(data);
          } catch (_e) {
            // silently ignore callback errors
          }
        });
      }
    );

    this.socket.on(
      "notification:unread_count",
      (data: { unreadCount: number }) => {
        this.unreadCountCallbacks.forEach((callback) => {
          try {
            callback(data);
          } catch (_e) {
            // silently ignore callback errors
          }
        });
      }
    );

    this.socket.on("disconnect", () => {});
  }

  onNewNotification(
    callback: (data: { notification: any }) => void,
    listenerId?: string
  ): string {
    const id =
      listenerId ||
      `listener_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.notificationCallbacks.set(id, callback);
    return id;
  }

  offNewNotification(listenerId: string) {
    this.notificationCallbacks.delete(listenerId);
  }

  onUnreadCountUpdate(
    callback: (data: { unreadCount: number }) => void,
    listenerId?: string
  ): string {
    const id =
      listenerId ||
      `listener_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.unreadCountCallbacks.set(id, callback);
    return id;
  }

  offUnreadCountUpdate(listenerId: string) {
    this.unreadCountCallbacks.delete(listenerId);
  }

  removeListenersByPrefix(prefix: string) {
    for (const [id] of this.notificationCallbacks) {
      if (id.startsWith(prefix))
        this.notificationCallbacks.delete(id);
    }
    for (const [id] of this.unreadCountCallbacks) {
      if (id.startsWith(prefix))
        this.unreadCountCallbacks.delete(id);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.userId = null;
    }
  }
}

export const notificationSocketService = new NotificationSocketService();
